import { apiCall } from "../../../../../../_services/apiCall";
import config from "../../../../../../config/config";

const COMPANY = config.POSTLOGIN_API_URL_COMPANY;

/**
 * Fetches every piece of data the Excel template depends on, in parallel
 * wherever possible. Returns a normalized bundle the shaper consumes.
 *
 * Mirrors what backend `generateExcelTemplate` + `fetchAllowedReportingQuestions`
 * used to do server-side, but uses the equivalent already-exposed endpoints.
 * Derived (formula-based) KPIs are excluded — their value is computed from
 * their sub-questions, so they are never filled in the data-entry template
 * (the sub-questions themselves remain, as they are ordinary questions).
 */
export const fetchTemplateData = async ({ financialYearId }) => {
  const userId = JSON.parse(localStorage.getItem("user_temp_id"));

  const [emissionRes, unitMasterRes, unitKpiRes, evidenceRes, assetRes, frameworkRes] =
    await Promise.all([
      apiCall(`${COMPANY}emissionKpiSettings`, {}, { fyid: financialYearId }, "GET", false, false),
      apiCall(`${COMPANY}getUnitMaster`, {}, {}, "GET", false, false),
      apiCall(`${COMPANY}getUnitKpiSettings`, {}, { fyid: financialYearId }, "GET", false, false),
      apiCall(`${COMPANY}settings/kpis/required-evidences`, {}, { fyid: financialYearId }, "GET", false, false),
      apiCall(`${COMPANY}kpiAssetSettings`, {}, { fyid: financialYearId }, "GET", false, false),
      apiCall(`${COMPANY}getFramework`, {}, { type: "ALL", userId }, "GET", false, false),
    ]);

  const emissionRaw = emissionRes?.data;
  const allowedKpiIds = Array.isArray(emissionRaw?.kpiIds)
    ? emissionRaw.kpiIds.map(Number)
    : [];
  if (!allowedKpiIds.length) {
    throw new Error("No KPIs enabled for this company in the selected financial year.");
  }

  const frameworks = frameworkRes?.data?.data ?? [];
  const frameworkIds = frameworks.map((f) => f.id);

  const reportingRes = await apiCall(
    `${COMPANY}getReportingQuestionnew`,
    {},
    {
      financialYearId,
      frameworkIds,
      question_type_filter: JSON.stringify(["quantitative"]),
    },
    "GET",
    false,
    false,
  );

  const allowedSet = new Set(allowedKpiIds);
  const reportingQuestions = (reportingRes?.data?.data ?? []).filter(
    (q) => allowedSet.has(Number(q.questionId)) && !q.isFormulaBased,
  );
  if (!reportingQuestions.length) {
    throw new Error("No enabled KPIs returned any reporting questions for this company.");
  }

  const unitMasterRaw = unitMasterRes?.data;
  const unitMasterList = Array.isArray(unitMasterRaw)
    ? unitMasterRaw
    : Array.isArray(unitMasterRaw?.data)
      ? unitMasterRaw.data
      : [];

  const unitKpiList = unitKpiRes?.data?.data ?? [];
  const mappingByKpi = new Map();
  for (const m of unitKpiList) {
    mappingByKpi.set(Number(m.kpiId), m);
  }

  const evidenceMappings = evidenceRes?.data?.data ?? [];

  const assetRecords = assetRes?.data?.data ?? [];

  return {
    reportingQuestions,
    unitMasterList,
    mappingByKpi,
    evidenceMappings,
    assetRecords,
  };
};
